import React, { useState, useEffect, useContext } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Filter, Layers, AlertTriangle, CheckCircle, Info, Gauge, ChevronRight, ArrowLeft, Droplets, Wind, Thermometer } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';

// Category icons and colors - aligned with Calculator categories
const categoryStyles = {
  'Softwoods': { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: '🌲' },
  'Hardwoods': { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: '🪵' },
  'Exotic Woods': { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: '🌴' },
  'Sheet Goods': { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: '📋' },
  'Plastics': { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: '🧪' },
  'Metals': { color: 'bg-gray-500/20 text-zinc-400 border-gray-500/30', icon: '⚙️' },
  'Composites & Foam': { color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', icon: '🔬' },
};

const hardnessColors = {
  'Soft': 'bg-green-500/20 text-green-400',
  'Medium': 'bg-yellow-500/20 text-yellow-400',
  'Hard': 'bg-orange-500/20 text-orange-400',
  'Very Hard': 'bg-red-500/20 text-red-400',
};

const machinabilityColors = {
  'Easy': 'bg-green-500/20 text-green-400',
  'Moderate': 'bg-yellow-500/20 text-yellow-400',
  'Difficult': 'bg-orange-500/20 text-orange-400',
  'Challenging': 'bg-red-500/20 text-red-400',
};

const MaterialDB = () => {
  const { token } = useContext(AuthContext);
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [categories, setCategories] = useState({});
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState('all');
  const [hardnessFilter, setHardnessFilter] = useState('all');
  const [machinabilityFilter, setMachinabilityFilter] = useState('all');
  
  // Available subcategories based on selected category
  const [availableSubcategories, setAvailableSubcategories] = useState([]);

  useEffect(() => {
    fetchMaterials();
    fetchCategories();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [materials, searchQuery, categoryFilter, subcategoryFilter, hardnessFilter, machinabilityFilter]);

  useEffect(() => {
    if (categoryFilter === 'all') {
      setAvailableSubcategories([]);
      setSubcategoryFilter('all');
    } else if (categories[categoryFilter]) {
      setAvailableSubcategories(categories[categoryFilter]);
      setSubcategoryFilter('all');
    }
  }, [categoryFilter, categories]);

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials`);
      setMaterials(response.data);
    } catch (error) {
      console.error('Failed to fetch materials:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/materials/categories/list`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const applyFilters = () => {
    let result = [...materials];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(m => 
        m.name.toLowerCase().includes(query) ||
        m.category.toLowerCase().includes(query) ||
        m.subcategory.toLowerCase().includes(query)
      );
    }
    
    if (categoryFilter !== 'all') {
      result = result.filter(m => m.category === categoryFilter);
    }
    
    if (subcategoryFilter !== 'all') {
      result = result.filter(m => m.subcategory === subcategoryFilter);
    }
    
    if (hardnessFilter !== 'all') {
      result = result.filter(m => m.hardness === hardnessFilter);
    }
    
    if (machinabilityFilter !== 'all') {
      result = result.filter(m => m.machinability === machinabilityFilter);
    }
    
    setFilteredMaterials(result);
  };

  // Material detail view
  if (selectedMaterial) {
    const style = categoryStyles[selectedMaterial.category] || { color: 'bg-gray-500/20 text-zinc-400', icon: '📦' };
    
    return (
      <div className="space-y-6">
        <Button 
          variant="ghost" 
          onClick={() => setSelectedMaterial(null)}
          className="text-[#e67e22] hover:text-[#e67e22]/80 hover:bg-black/60"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Materials
        </Button>

        <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={style.color}>{selectedMaterial.category}</Badge>
                      <Badge variant="outline" className="text-[#d6d6d6] border-[#3a3a3a]">{selectedMaterial.subcategory}</Badge>
                    </div>
                    <CardTitle className="text-2xl text-white">{selectedMaterial.name}</CardTitle>
                  </div>
                  <div className="text-4xl">{style.icon}</div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Properties */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4 text-center">
                    <div className="text-xs text-[#a3a3a3] uppercase mb-1">Hardness</div>
                    <Badge className={hardnessColors[selectedMaterial.hardness]}>{selectedMaterial.hardness}</Badge>
                  </div>
                  <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4 text-center">
                    <div className="text-xs text-[#a3a3a3] uppercase mb-1">Density</div>
                    <span className="text-white font-medium">{selectedMaterial.density || 'N/A'}</span>
                  </div>
                  <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4 text-center">
                    <div className="text-xs text-[#a3a3a3] uppercase mb-1">Machinability</div>
                    <Badge className={machinabilityColors[selectedMaterial.machinability]}>{selectedMaterial.machinability}</Badge>
                  </div>
                  <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4 text-center">
                    <div className="text-xs text-[#a3a3a3] uppercase mb-1">Chip Type</div>
                    <span className="text-white font-medium">{selectedMaterial.chip_type}</span>
                  </div>
                </div>

                {/* Special Requirements */}
                <div className="flex flex-wrap gap-3">
                  {selectedMaterial.requires_coolant && (
                    <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
                      <Droplets className="w-3 h-3 mr-1" />
                      Requires Coolant
                    </Badge>
                  )}
                  {selectedMaterial.requires_dust_extraction && (
                    <Badge className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                      <Wind className="w-3 h-3 mr-1" />
                      Dust Extraction Required
                    </Badge>
                  )}
                  {selectedMaterial.melting_point && (
                    <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                      <Thermometer className="w-3 h-3 mr-1" />
                      Melting Point: {selectedMaterial.melting_point}
                    </Badge>
                  )}
                  {selectedMaterial.burn_threshold_rpm && (
                    <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/30">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Burn Risk &gt; {selectedMaterial.burn_threshold_rpm} RPM
                    </Badge>
                  )}
                </div>

                {/* Recommended Cutting Parameters */}
                <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4">
                  <div className="text-xs text-[#e67e22] font-semibold uppercase mb-4">Recommended Cutting Parameters</div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-[#a3a3a3] mb-1">Chipload (mm)</div>
                      <div className="text-lg font-mono text-white">
                        {selectedMaterial.recommended_chipload_range?.min} - {selectedMaterial.recommended_chipload_range?.max}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-[#a3a3a3] mb-1">RPM Range</div>
                      <div className="text-lg font-mono text-white">
                        {selectedMaterial.recommended_rpm_range?.min?.toLocaleString()} - {selectedMaterial.recommended_rpm_range?.max?.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-[#a3a3a3] mb-1">Feed Rate (mm/min)</div>
                      <div className="text-lg font-mono text-white">
                        {selectedMaterial.recommended_feed_rate_range?.min?.toLocaleString()} - {selectedMaterial.recommended_feed_rate_range?.max?.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-[#a3a3a3] mb-1">Depth of Cut (mm)</div>
                      <div className="text-lg font-mono text-white">
                        {selectedMaterial.recommended_doc_range?.min} - {selectedMaterial.recommended_doc_range?.max}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-[#a3a3a3] mb-1">Stepover (%)</div>
                      <div className="text-lg font-mono text-white">
                        {selectedMaterial.recommended_stepover_percent?.min} - {selectedMaterial.recommended_stepover_percent?.max}%
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-[#a3a3a3] mb-1">Recommended Flutes</div>
                      <div className="text-lg font-mono text-white">
                        {selectedMaterial.recommended_flute_count?.join(', ')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Compatible Tool Categories */}
                <div className="bg-black/50 border border-[#3a3a3a] rounded-lg p-4">
                  <div className="text-xs text-[#a3a3a3] font-semibold uppercase mb-3">Compatible Tool Types</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedMaterial.compatible_tool_categories?.map((cat, i) => (
                      <Link key={i} to={`/tools/bits?category=${encodeURIComponent(cat)}`}>
                        <Badge 
                          variant="outline" 
                          className="text-[#d6d6d6] border-[#3a3a3a] hover:border-[#e67e22] hover:text-[#e67e22] cursor-pointer transition-colors"
                        >
                          {cat}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                  {selectedMaterial.preferred_tool_coating && (
                    <div className="mt-3 text-sm text-[#a3a3a3]">
                      Preferred coating: <span className="text-white">{selectedMaterial.preferred_tool_coating}</span>
                    </div>
                  )}
                </div>

                {/* Tips */}
                {selectedMaterial.tips?.length > 0 && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                      <div>
                        <div className="font-semibold text-green-400">Tips</div>
                        <ul className="text-green-200/80 text-sm mt-1 space-y-1">
                          {selectedMaterial.tips.map((t, i) => (
                            <li key={i}>• {t}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {selectedMaterial.warnings?.length > 0 && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                      <div>
                        <div className="font-semibold text-red-400">Warnings</div>
                        <ul className="text-red-200/80 text-sm mt-1 space-y-1">
                          {selectedMaterial.warnings.map((w, i) => (
                            <li key={i}>• {w}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Link to="/calculator" className="flex-1">
                    <Button className="w-full bg-[#e67e22] hover:bg-[#e67e22]/90 text-black font-bold">
                      <Gauge className="w-4 h-4 mr-2" />
                      Calculate Feed & Speed
                    </Button>
                  </Link>
                  <Link to="/tools/bits">
                    <Button variant="outline" className="border-[#3a3a3a] text-[#d6d6d6]">
                      View Compatible Tools
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
      </div>
    );
  }

  // Group materials by category for tabbed view
  const materialsByCategory = filteredMaterials.reduce((acc, mat) => {
    if (!acc[mat.category]) acc[mat.category] = [];
    acc[mat.category].push(mat);
    return acc;
  }, {});

  // Main material list view
  return (
    <div 
      data-testid="material-db-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.FEEDS_SPEEDS)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/tools" className="text-[#e67e22] hover:text-[#e67e22]/80 text-sm flex items-center gap-1 mb-2">
          <ArrowLeft className="w-4 h-4" /> Back to Tools
        </Link>
        <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">MATERIAL REFERENCE</div>
        <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tight text-white mb-2">
          MATERIAL DATABASE
        </h1>
        <p className="text-lg text-[#d6d6d6]">
          Comprehensive reference for chiploads, speeds, and cutting parameters by material type
        </p>
      </div>

      {/* Filters */}
      <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
                <Input
                  placeholder="Search materials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3]"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                <SelectItem value="all" className="text-white">All Categories</SelectItem>
                {Object.keys(categories).map(cat => (
                  <SelectItem key={cat} value={cat} className="text-white">{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableSubcategories.length > 0 && (
              <Select value={subcategoryFilter} onValueChange={setSubcategoryFilter}>
                <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                  <SelectValue placeholder="Subcategory" />
                </SelectTrigger>
                <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                  <SelectItem value="all" className="text-white">All Subcategories</SelectItem>
                  {availableSubcategories.map(sub => (
                    <SelectItem key={sub} value={sub} className="text-white">{sub}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={hardnessFilter} onValueChange={setHardnessFilter}>
              <SelectTrigger className="bg-black/50 border-[#3a3a3a] text-white">
                <SelectValue placeholder="Hardness" />
              </SelectTrigger>
              <SelectContent className="bg-[#1f1f1f] border-[#3a3a3a]">
                <SelectItem value="all" className="text-white">All Hardness</SelectItem>
                <SelectItem value="Soft" className="text-white">Soft</SelectItem>
                <SelectItem value="Medium" className="text-white">Medium</SelectItem>
                <SelectItem value="Hard" className="text-white">Hard</SelectItem>
                <SelectItem value="Very Hard" className="text-white">Very Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="text-[#a3a3a3] text-sm">
        Showing {filteredMaterials.length} of {materials.length} materials
      </div>

      {/* Tabbed Material View */}
      {categoryFilter === 'all' && Object.keys(materialsByCategory).length > 0 ? (
        <Tabs defaultValue={Object.keys(materialsByCategory)[0]} className="space-y-4">
          <TabsList className="bg-black/60 border border-[#3a3a3a] p-1 flex-wrap h-auto">
            {Object.keys(materialsByCategory).map(cat => {
              const style = categoryStyles[cat] || { icon: '📦' };
              return (
                <TabsTrigger 
                  key={cat} 
                  value={cat}
                  className="data-[state=active]:bg-[#e67e22] data-[state=active]:text-black"
                >
                  <span className="mr-1">{style.icon}</span>
                  {cat} ({materialsByCategory[cat].length})
                </TabsTrigger>
              );
            })}
          </TabsList>

          {Object.entries(materialsByCategory).map(([cat, mats]) => (
            <TabsContent key={cat} value={cat}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mats.map(material => (
                  <MaterialCard 
                    key={material.id} 
                    material={material} 
                    onClick={() => setSelectedMaterial(material)}
                  />
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMaterials.map(material => (
            <MaterialCard 
              key={material.id} 
              material={material} 
              onClick={() => setSelectedMaterial(material)}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredMaterials.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Layers className="w-16 h-16 text-[#a3a3a3] mx-auto mb-4" />
          <p className="text-[#d6d6d6] text-lg">No materials match your filters</p>
          <Button 
            variant="ghost" 
            onClick={() => {
              setSearchQuery('');
              setCategoryFilter('all');
              setSubcategoryFilter('all');
              setHardnessFilter('all');
              setMachinabilityFilter('all');
            }}
            className="text-[#e67e22] mt-2"
          >
            Clear all filters
          </Button>
        </div>
      )}
      </div>
    </div>
  );
};

// Material Card Component
const MaterialCard = ({ material, onClick }) => {
  const style = categoryStyles[material.category] || { color: 'bg-gray-500/20 text-zinc-400', icon: '📦' };
  
  return (
    <Card 
      onClick={onClick}
      className="bg-black/60 backdrop-blur-sm border-[#3a3a3a] hover:border-[#e67e22]/50 transition-colors cursor-pointer group"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={style.color}>{material.subcategory}</Badge>
            <Badge className={hardnessColors[material.hardness]}>{material.hardness}</Badge>
          </div>
          <span className="text-2xl">{style.icon}</span>
        </div>
        <CardTitle className="text-white group-hover:text-[#e67e22] transition-colors text-lg mt-2">
          {material.name}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
          <div>
            <span className="text-[#a3a3a3]">Chipload:</span>
            <span className="text-white ml-1">{material.recommended_chipload_range?.min}-{material.recommended_chipload_range?.max}</span>
          </div>
          <div>
            <span className="text-[#a3a3a3]">RPM:</span>
            <span className="text-white ml-1">{(material.recommended_rpm_range?.min/1000).toFixed(0)}k-{(material.recommended_rpm_range?.max/1000).toFixed(0)}k</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mb-3">
          {material.compatible_tool_categories?.slice(0, 3).map((cat, i) => (
            <Badge key={i} variant="outline" className="text-xs text-[#a3a3a3] border-[#3a3a3a]">
              {cat}
            </Badge>
          ))}
          {material.compatible_tool_categories?.length > 3 && (
            <Badge variant="outline" className="text-xs text-[#a3a3a3] border-[#3a3a3a]">
              +{material.compatible_tool_categories.length - 3}
            </Badge>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {material.requires_coolant && (
            <Badge className="bg-blue-500/20 text-blue-400 text-xs">
              <Droplets className="w-3 h-3 mr-1" />
              Coolant
            </Badge>
          )}
          {material.melting_point && (
            <Badge className="bg-red-500/20 text-red-400 text-xs">
              <Thermometer className="w-3 h-3 mr-1" />
              Heat sensitive
            </Badge>
          )}
        </div>

        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#3a3a3a]">
          <Badge className={machinabilityColors[material.machinability]}>
            {material.machinability}
          </Badge>
          <ChevronRight className="w-4 h-4 text-[#e67e22] opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </CardContent>
    </Card>
  );
};

export default MaterialDB;
